<template>
  <div class="fts-solution comfort-form-design" :data-solution-load="loading || aILoading ? 'loading' : 'loaded'">
    <div class="solution-question">
      <div class="solution-question-label">
        I want
      </div>
      <input
        type="text"
        class="fts-form-control"
        placeholder="to improve procurement process"
        maxlength="300"
        v-model="searchText"
        @input="searchSolution"
      >
    </div>

    <template v-if="aIMode">
      <div class="solution-container">
        <fts-release-badge style="position: absolute;top: 0.25rem;right: 0.25rem;user-select: none;" />

        <template v-if="solution && !solution.search">
          <div class="solution-header">
            <div class="solution-info">
              <fts-moji :emoji="solution.emoji" :size="18" class="solution-emoji" v-if="solution.emoji" />
              <div class="solution-name">{{solution.name}}</div>
            </div>
            
            <fts-text-truncate
              :line-clamp="3"
              :value="solution.description"
              class="solution-description"
              v-if="solution.description"
            />
          </div>

          <div class="solution-content">
            <div class="solution-console solution-console--flow">
              <div class="solution-console-title">Flow</div>
              <div class="solution-diagram-wrapper">
                <fts-flow-diagram
                  expandable
                  :diagram-data="{
                    'name': solution.name,
                    'emoji': solution.emoji,
                    'states': solution.states,
                    'activities': solution.activities,
                    'flows': solution.flows,
                    'diagram': solution.diagram
                  }"
                />
              </div>
            </div>

            <div class="solution-console solution-console--information">
              <div class="solution-console-title">Information</div>
              <fts-solution-information
                v-for="x in solution.information"
                :key="x.id"
                :item="x"
              />
            </div>
          </div>
        </template>

        <div class="solution-container-spinner" v-else-if="aILoading">
          <fts-spinner show :size="32" />
          <div class="solution-container-spinner-description">
            <div>It takes a while. Please be patient.</div>
            <div>{{ jumbotronWords[jumbotronIndex] }}</div>
          </div>
        </div>

        <div class="solution-not-found" v-else>
          <div class="solution-not-found-title">No Solutions Found</div>
          <div class="solution-not-found-description">
            <div>Try adjust your search term, or</div>
            <div class="solution-not-found-action">
              <fts-btn
                lg
                color="primary"
                outline
                text-transform-init
                class="fts-font-normal"
                @click="getAISolution(true)"
              >
                Try again
              </fts-btn>
            </div>
          </div>
        </div>
      </div>

      <div class="solution-action" v-if="!aILoading">
        <fts-btn
          lg
          color="primary"
          outline
          text-transform-init
          class="fts-font-normal"
          @click="getAISolution(true)"
          v-if="solution"
        >
          Try other solution
        </fts-btn>

        <fts-btn lg text text-transform-init class="fts-font-normal" @click="backToSelection">
          Back to Selection
        </fts-btn>
      </div>
    </template>

    <template v-else-if="searchTouch">
      <div class="solution-container solution-container--ready-solution fts-scrollbar">
        <div class="solution-container-overlay" v-if="loading">
          <fts-spinner show :size="32" class="solution-container-overlay-spinner" />
        </div>

        <template v-if="solutions && solutions.length">
          <div
            v-for="(x, ind) in solutions"
            :key="`${x.id}-${ind}`"
            class="solution-option"
            @click="selectSolution(x.id)"
          >
            <div class="solution-option-checkbox-wrapper">
              <div
                class="solution-option-checkbox"
                :class="{'solution-option-checkbox--active': solutionId === x.id}"
              >
                <font-awesome-icon :icon="['fas', 'check']" v-if="solutionId === x.id" />
              </div>
            </div>
            <div class="solution-option-wrapper">
              <div class="solution-option-name">
                {{x.name}}
              </div>
              <div class="solution-option-description">
                {{x.description}}
              </div>
            </div>
          </div>
        </template>

        <div class="solution-not-found" v-else>
          <div class="solution-not-found-title">No Solutions Found</div>
          <div class="solution-not-found-description">
            <div>There are no solutions that match your current filters.</div>
            <div>Try adjust your search term.</div>
          </div>
        </div>
      </div>

      <div class="solution-action" v-if="searchText">
        <fts-btn
          lg
          color="primary"
          outline
          text-transform-init
          :loading="aILoading"
          class="fts-font-normal"
          @click="switchAIMode"
        >
          Ask AI to give solution&nbsp;<fts-release-badge />
        </fts-btn>
      </div>
    </template>

    <div class="solution-history" v-if="solutionSearchResults && solutionSearchResults.length">
      <div class="solution-history-label">Solution history</div>
      <div class="solution-history-container">
        <div
          v-for="(x, index) in solutionSearchResults"
          :key="`${x.aiId}-${index}`"
          class="solution-history-item"
          :class="{'solution-history-item--active': x.aiId === (solution && solution.aiId)}"
          @click="selectAISolution(x)"
        >
          <template v-if="x.search">
            <div class="solution-history-item__search">
              <fts-spinner :size="14" show style="margin-right: 0.375rem;"/> It takes a while. Please be patient.
            </div>
            <div class="solution-history-generate-date">
              {{ jumbotronWords[jumbotronIndex] }}
            </div>
          </template>
          <template v-else>
            <fts-label-group
              :item="{ 'id': x.aiId, 'name': x.name, 'emoji': x.emoji}"
              emoji
              :emoji-size="14"
            />
            <div class="solution-history-generate-date" v-if="x.generatedOn">
              at {{moment(x.generatedOn).format('h:mm:ss A')}}
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

import { clientStore } from '../../store/clientStore';
import commons from '../../commons/commons';

import FTSSolutionInformation from './FTSSolutionInformation';
import FTSBtn from '../FTSBtn';
import FTSFlowDiagram from '../FTSFlowDiagram';
import FTSMoji from '../FTSMoji';
import FTSSpinner from '../FTSSpinner';
import FTSTextTruncate from '../FTSTextTruncate';
import FTSLabelGroup from '../FTSLabelGroup';

export default {
  components: {
    "fts-solution-information": FTSSolutionInformation,
    "fts-btn": FTSBtn,
    "fts-flow-diagram": FTSFlowDiagram,
    "fts-moji": FTSMoji,
    "fts-spinner": FTSSpinner,
    "fts-text-truncate": FTSTextTruncate,
    "fts-label-group": FTSLabelGroup
  },
  props: {
    solutionId: [String, Number],
    aIMode: Boolean
  },
  data: () => ({
    count: 0,
    loading: false,
    aILoading: false,
    solution: null,
    solutions: [],
    solutionSearchResults: [],
    searchText: null,
    searchTouch: false,
    jumbotronWords: Object.freeze([
      'understanding your requirements', 'designing possible solutions that fits', 
      'generating information that system stored', 'generating activities that we performed',
      'generating states that shows the status','generating flows so everyone can follow',
      'presenting solution',
    ]),
    jumbotronIndex: 0,
    jumbotronInterval: null
  }),
  methods: {
    moment,
    async loadSolutions() {
      try {
        this.loading = true;
        const res = await clientStore.dispatch("loadWorkspaceSolutions", {
          search: this.searchText
        });
        if (res.data) {
          this.solutions = res.data;
        } else {
          this.solutions = [];
        }
      } catch(err) {
        console.error(err);
        this.solutions = [];
      } finally {
        this.loading = false;
      }

      this.$emit("update:solutionId", null);
    },
    searchSolution: _.debounce(async function() {
      this.searchTouch = true;
      this.selectSolution(null);
      this.resetAISolution();
      this.loadSolutions();
    }, 300),
    selectSolution(solutionId) {
      if (solutionId === this.solutionId) {
        this.$emit("update:solutionId", null);
      } else {
        this.$emit("update:solutionId", solutionId);
      }
    },
    async getAISolution(other) {
      if (!this.aILoading) {
        let requestRetry = false;
        const getJumbotronIndex = () => Math.floor(Math.random() * this.jumbotronWords.length);
        const clearTemporarySearch = () => {
          this.solutionSearchResults = this.solutionSearchResults.filter((x) => !x.search);
        }
        const loadAISolution = async (payload) => {
          try {
            const res = await clientStore.dispatch("loadAISolution", {
              "search": payload.search,
              "count": this.count
            });

            if (!res.data.name) {
              throw 'solution invalid';
            }

            clearTemporarySearch();
 
            if (!this.solutionSearchResults.some((x) => x.aiId === res.data.aiId)) {
              this.solutionSearchResults.push({
                ...res.data,
                generatedOn: moment()
              });
            }

            this.aIMode && this.setAISolution(res.data);
          } catch(err) {
            if (requestRetry) {
              throw err;
            } else {
              requestRetry = true;
            }
          }
        }

        try {
          this.aILoading = true;
          const temporarySearch = { aiId: commons.nanoID(), search: true };
          this.solutionSearchResults.push(temporarySearch);
          this.solution = temporarySearch;
          this.jumbotronIndex = getJumbotronIndex();
          this.jumbotronInterval = setInterval(() => {
            this.jumbotronIndex = getJumbotronIndex();
          }, 2000);

          if (other) {
            this.count = this.count + 1;
          }

          const payload = { "search": this.searchText };
          await loadAISolution(payload);
          requestRetry && await loadAISolution(payload);
        } catch(err) {
          console.error(err);
          this.solution = null;
          clearTemporarySearch();
          this.$emit("update:solutionId", null);
        } finally {
          this.aILoading = false;
          clearInterval(this.jumbotronInterval);
          this.jumbotronInterval = null;
        }
      }
    },
    switchAIMode() {
      this.$emit("update:aIMode", true);
      this.getAISolution();
    },
    resetAISolution() {
      this.$emit("update:aIMode", false);
      this.solution = null;
    },
    setAISolution(item) {
      this.solution = {...item};
      this.$emit("update:solutionId", item.aiId);
    },
    selectAISolution(item) {
      if (!this.aIMode) {
        this.$emit("update:aIMode", true);
      }

      if (item.search) {
        this.solution = item;
      } else {
        this.setAISolution(item);
      }
    },
    backToSelection() {
      this.resetAISolution();
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-solution {
  .solution-question {
    display: flex;
    flex-wrap: wrap;
    .solution-question-label {
      margin-right: 0.75rem;
      padding-top: calc(0.5rem + 1px);
      padding-bottom: calc(0.5rem + 1px);
      font-size: 1.25rem;
      line-height: 1.75rem;
      white-space: nowrap;
      color: var(--fts-text);
    }
    .fts-form-control {
      flex: 1 1 250px;
      font-size: 1.25rem;
      line-height: 1.75rem;
      @include placeholder($fontSize: 1.25rem);
    }
  }
  .solution-container {
    position: relative;
    min-height: 5rem;
    margin-top: 1.5rem;
    &.solution-container--ready-solution {
      max-height: 450px;
      border: 1px solid var(--fts-border);
      border-radius: $comfort-border-radius;
    }
  }
  .solution-container-overlay {
    position: absolute;
    height: 100%;
    width: 100%;
    .solution-container-overlay-spinner {
      padding-top: 1.5rem;
    }
  }
  .solution-container-spinner {
    padding: 2rem 1rem;
    text-align: center;
    .solution-container-spinner-description {
      margin-top: 1.25rem;
      font-size: 0.875rem;
      line-height: 1.25rem;
      > *:not(:first-child) {
        margin-top: 0.25rem;
      }
    }
  }
  .solution-action {
    @include flex-gap($gapTop: 0.5rem, $gapLeft: 0.25rem);
    padding-top: 1.5rem;
  }
  .solution-option {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 0.75rem 0.75rem;
    cursor: pointer;
    .solution-option-name {
      font-size: 1rem;
      line-height: 1.5rem;
      word-break: break-word;
    }
    .solution-option-description {
      font-size: 0.875rem;
      line-height: 1.25rem;
      word-break: break-word;
      color: var(--fts-default);
    }
    & + .solution-option {
      border-top: 1px solid var(--fts-border);
    }
  }
  .solution-option-checkbox-wrapper {
    height: 1.5rem;
    display: flex;
    align-items: center;
  }
  .solution-option-checkbox {
    height: 1rem;
    width: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--fts-default);
    border-radius: 10em;
    > svg {
      font-size: 0.625rem;
    }
    &.solution-option-checkbox--active {
      border-color: var(--fts-success);
      background-color: var(--fts-success);
      color: var(--fts-light);
    }
  }
  .solution-option-wrapper {
    flex: 1;
    margin-left: 0.75rem;
  }
  .solution-not-found {
    padding: 2rem 1rem;
    text-align: center;
  }
  .solution-not-found-title {
    margin-bottom: 0.25rem;
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--fts-text);
  }
  .solution-not-found-description {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-default);
  }
  .solution-not-found-action {
    margin-top: 0.75rem;
  }
  .solution-header {
    margin-bottom: 1.5rem;
  }
  .solution-info {
    display: flex;
    align-items: center;
    & + .solution-description {
      margin-top: 0.25rem;
    }
  }
  .solution-emoji {
    & + .solution-name {
      margin-left: 0.5rem;
    }
  }
  .solution-name {
    font-weight: 500;
    font-size: 1.125rem;
    line-height: 1.75rem;
    color: var(--fts-text);
  }
  .solution-description {
    color: var(--fts-default);
  }
  .solution-content {
    @include flex-gap($gap: 1rem);
  }
  .solution-console {
    flex: 1 1 100%;
    max-width: calc(100% - 1rem);
  }
  .solution-console-title {
    margin-bottom: 0.5rem;
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--fts-text);
  }
  .solution-diagram-wrapper {
    height: 450px;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--fts-border);
    border-radius: $comfort-border-radius;
    overflow: hidden;
  }
  .solution-history {
    margin-top: 2rem;
  }
  .solution-history-label {
    margin-bottom: 0.5rem;
    font-size: 1rem;
    line-height: 1.5rem;
  }
  .solution-history-container {
    @include flex-gap($gap: 0.5rem);
  }
  .solution-history-item {
    max-width: calc(100% - 0.5rem);
    padding: 0.375rem 0.75rem;
    border: 1px solid var(--fts-border);
    border-radius: $comfort-border-radius;
    font-size: 0.875rem;
    line-height: 1.25rem;
    cursor: pointer;
    &.solution-history-item--active {
      border-color: var(--fts-primary);
      background-color: var(--fts-primary-hover);
    }
  }
  .solution-history-item__search {
    display: flex;
    align-items: center;
  }
  .solution-history-generate-date {
    margin-top: 0.125rem;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--fts-default);
  }

  @media (min-width: 640px) {
    .solution-content {
      flex-direction: row-reverse;
    }
    .solution-console {
      flex: 1;
    }
    .solution-console--information {
      max-width: calc(30% - 1rem);
    }
    .solution-console--flow {
      max-width: calc(70% - 1rem);
    }
  }
  @media (min-width: 768px) {
    .solution-console--information {
      max-width: calc(25% - 1rem);
    }
    .solution-console--flow {
      max-width: calc(75% - 1rem);
    }
  }
  @media (max-width: 640px) {
    .solution-question {
      display: flex;
      flex-wrap: wrap;
      .solution-question-label {
        margin-right: 0.75rem;
        padding-top: calc(0.5rem + 1px);
        padding-bottom: calc(0.5rem + 1px);
        font-size: 1rem;
        line-height: 1.5rem;
        white-space: nowrap;
      }
      .fts-form-control {
        flex: 1 1 250px;
        font-size: 1rem;
        line-height: 1.5rem;
        @include placeholder($fontSize: 1rem);
      }
    }
    .solution-action {
      > * {
        flex: 1 1 100%;
      }
    }
    .solution-history-item {
      flex: 1 1 100%;
    }
  }
}
</style>